import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom Icons
const BASKET_ICON_URL = "/images/optimized/basket-v1.webp";
const FARMER_ICON_URL = "/images/optimized/account-v1.webp";
const BARN_MENU_ICON_URL = "/images/optimized/menu-v1.webp";

// Farmer Account Icon - same visual size as basket
const AccountIcon = () => (
  <img 
    src={FARMER_ICON_URL} 
    alt="Account" 
    className="w-[58px] h-[58px] sm:w-[66px] sm:h-[66px]"
    style={{ 
      objectFit: 'contain',
      opacity: 0.75
    }}
  />
);

// Farm Basket Icon
const FarmBasketIcon = () => (
  <img 
    src={BASKET_ICON_URL} 
    alt="Basket" 
    className="w-[62px] h-[62px] sm:w-[70px] sm:h-[70px]"
    style={{ 
      objectFit: 'contain',
      opacity: 0.75
    }}
  />
);

// Barn Menu Icon - 5% larger
const BarnMenuIcon = () => (
  <img 
    src={BARN_MENU_ICON_URL} 
    alt="Menu" 
    className="w-[74px] h-[74px] sm:w-[84px] sm:h-[84px]"
    style={{ 
      objectFit: 'contain',
      opacity: 0.75
    }}
  />
);

// Heart Favorites Icon - matching other icons size and weight
const HeartIcon = () => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="#0f1d2f"
    strokeWidth="0.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-[23px] h-[23px] sm:w-[26px] sm:h-[26px]"
    style={{ opacity: 0.75 }}
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

// Hamburger Menu Icon
const MenuIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="1.2" 
    className={className}
  >
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

// Close Icon
const CloseIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="1.2" 
    className={className}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if we're on admin pages
  const isAdminPage = location.pathname.startsWith('/admin');

  // Fetch cart count
  useEffect(() => {
    const fetchCartCount = async () => {
      if (!isAuthenticated) {
        setCartCount(0);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/cart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const totalItems = response.data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
        setCartCount(totalItems);
      } catch (error) {
        setCartCount(0);
      }
    };
    fetchCartCount();
    
    // Listen for cart updates
    const handleCartUpdate = () => fetchCartCount();
    window.addEventListener('cart-updated', handleCartUpdate);
    return () => window.removeEventListener('cart-updated', handleCartUpdate);
  }, [isAuthenticated, location.pathname]);

  // Close menu on route change
  useEffect(() => {
    if (menuOpen) {
      setMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // Close menu on Escape — a11y: dialog-pattern menus must respect Escape
  // and the backdrop was previously the only closing path.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [menuOpen]);

  // Don't render header on admin pages (after all hooks)
  if (isAdminPage) {
    return null;
  }

  const handleAccountClick = () => {
    if (isAuthenticated) {
      // Navigate to farm dashboard if user has a farm, otherwise customer dashboard
      if (user?.has_farm) {
        navigate("/farm-dashboard");
      } else {
        navigate("/dashboard");
      }
    } else {
      navigate("/login");
    }
  };

  return (
    <>
      {/* Decorative Top Banner - Plain Dark Navy */}
      <div 
        className="fixed top-0 left-0 right-0 z-[51] h-8 sm:h-10"
        style={{ backgroundColor: '#0f1d2f' }}
      />

      {/* Header Bar */}
      <header 
        className="fixed top-8 sm:top-10 left-0 right-0 z-50" 
        style={{ 
          backgroundColor: '#ffffff',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)'
        }}
        data-testid="header"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Left: Barn Menu + Heart Favorites Icons - aligned with hero edges */}
            <div className="flex items-center" style={{ marginTop: '30px', marginLeft: '-4px' }}>
              <button 
                onClick={() => setMenuOpen(true)}
                className="hover:opacity-60 transition-opacity flex items-center"
                data-testid="menu-button"
                aria-label="Open menu"
              >
                <BarnMenuIcon />
              </button>
              <Link 
                to="/favorites"
                className="hover:opacity-50 transition-opacity flex items-center justify-center"
                style={{ marginLeft: '-18px' }}
                data-testid="favorites-button"
                aria-label="Favorites"
              >
                <HeartIcon />
              </Link>
            </div>

            {/* Center: Logo - increased white space above (50px margin) */}
            <Link to="/" className="absolute left-1/2 transform -translate-x-1/2 flex items-center" style={{ marginTop: '50px', marginBottom: '8px' }}>
              <img 
                src="/images/optimized/logo-wordmark-v3.webp"
                alt="American Farmstand"
                className="h-[118px] sm:h-[138px] lg:h-[158px] w-auto"
              />
            </Link>

            {/* Right: Cart + Account Icons - aligned with hero edges */}
            <div className="flex items-center" style={{ marginRight: '-4px', marginTop: '30px' }}>
              {/* Hit-box ends at the visual midpoint between basket and account icons so taps never fall through to Account */}
              <Link 
                to="/cart"
                className="hover:opacity-50 transition-opacity flex items-center relative z-10 w-[47px] -mr-[15px] sm:w-[54px] sm:-mr-[14px]"
                data-testid="cart-button"
                aria-label="Basket"
              >
                <span className="pointer-events-none flex shrink-0 -mr-[15px] sm:-mr-[16px]">
                  <FarmBasketIcon />
                </span>
                {cartCount > 0 && (
                  <span 
                    className="absolute flex items-center justify-center text-white text-xs font-bold rounded-full pointer-events-none"
                    style={{ 
                      backgroundColor: '#B6A46D',
                      minWidth: '20px',
                      height: '20px',
                      top: '8px',
                      right: '-7px',
                      padding: '0 5px',
                      fontFamily: 'Arial, sans-serif'
                    }}
                    data-testid="cart-count-badge"
                  >
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Link>
              <button 
                onClick={handleAccountClick}
                className="hover:opacity-50 transition-opacity flex items-center justify-center"
                data-testid="account-button"
                aria-label="Account"
              >
                <AccountIcon />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Slide-out Menu Panel */}
      <div 
        className={`fixed inset-0 z-[100] transition-all duration-500 ${
          menuOpen ? 'visible' : 'invisible'
        }`}
      >
        {/* Backdrop. Inert while closed so a stale/animating backdrop
            never intercepts clicks over the panel area. When open the
            panel sits on an explicit higher stacking layer (below). */}
        <div 
          className={`absolute inset-0 bg-black transition-opacity duration-500 ${
            menuOpen ? 'opacity-20 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />

        {/* Menu Panel — explicit `relative z-10` so the panel's own
            links always paint and receive pointer events ABOVE the
            sibling backdrop, independent of animation state. */}
        <div 
          className={`absolute left-0 top-0 bottom-0 w-full max-w-md bg-white transform transition-transform duration-500 ease-out relative z-10 ${
            menuOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
          }`}
          style={{ visibility: menuOpen ? 'visible' : 'hidden' }}
          data-testid="slide-menu"
        >
          {/* Menu Header */}
          <div className="flex items-center justify-between px-6 lg:px-10 h-16 lg:h-20 border-b" style={{ borderColor: 'rgba(30, 58, 95, 0.1)' }}>
            <span 
              className="text-sm tracking-widest uppercase"
              style={{ color: '#0f1d2f', fontFamily: "Arial, sans-serif" }}
            >
              Menu
            </span>
            <button 
              onClick={() => setMenuOpen(false)}
              className="p-2 -mr-2 hover:opacity-60 transition-opacity"
              aria-label="Close menu"
            >
              <CloseIcon className="w-6 h-6" style={{ color: '#0f1d2f' }} />
            </button>
          </div>

          {/* Menu Content */}
          <nav className="px-6 lg:px-10 py-10 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 80px)' }}>
            {/* Account Section - At Top */}
            <div className="mb-10 pb-8 border-b" style={{ borderColor: 'rgba(30, 58, 95, 0.1)' }}>
              {isAuthenticated ? (
                <div className="space-y-4">
                  <p 
                    className="text-sm"
                    style={{ color: '#0f1d2f', opacity: 0.6, fontFamily: "Arial, sans-serif" }}
                  >
                    Signed in as {user?.first_name}
                    {user?.has_farm && (
                      <span className="block text-xs mt-1" style={{ color: '#B6A46D' }}>
                        {user.farm_name}
                      </span>
                    )}
                  </p>
                  <Link 
                    to={user?.has_farm ? "/farm-dashboard" : "/dashboard"} 
                    className="block text-lg hover:opacity-60 transition-opacity"
                    style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
                  >
                    {user?.has_farm ? "Farm Dashboard" : "My Account"}
                  </Link>
                  {user?.has_farm && (
                    <Link 
                      to="/dashboard" 
                      className="block text-lg hover:opacity-60 transition-opacity"
                      style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
                    >
                      Customer Account
                    </Link>
                  )}
                  <button 
                    onClick={() => { logout(); setMenuOpen(false); }}
                    data-testid="header-signout-btn"
                    className="block text-lg hover:opacity-60 transition-opacity"
                    style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Link 
                    to="/login" 
                    className="block text-lg hover:opacity-60 transition-opacity"
                    style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
                  >
                    Sign In
                  </Link>
                  <Link 
                    to="/signup" 
                    className="block text-lg hover:opacity-60 transition-opacity"
                    style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
                  >
                    Create Account
                  </Link>
                </div>
              )}
            </div>

            {/* Main Navigation */}
            <div className="space-y-8">
              <Link 
                to="/" 
                className="block text-2xl tracking-wide hover:opacity-60 transition-opacity"
                style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
              >
                Home
              </Link>
              <Link 
                to="/our-marketplace" 
                className="block text-2xl tracking-wide hover:opacity-60 transition-opacity"
                style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
              >
                Our Marketplace
              </Link>
              
              {/* For Farms Section */}
              <div className="pt-4">
                <span 
                  className="text-xs tracking-widest uppercase block mb-4"
                  style={{ color: '#B6A46D', fontFamily: "Arial, sans-serif" }}
                >
                  For Farms
                </span>
                <div className="space-y-4 pl-4">
                  <Link 
                    to="/for-farms" 
                    className="block text-lg hover:opacity-60 transition-opacity"
                    style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
                  >
                    Explore More
                  </Link>
                  <Link 
                    to="/apply" 
                    className="block text-lg hover:opacity-60 transition-opacity"
                    style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
                  >
                    Apply to Sell
                  </Link>
                  <Link 
                    to="/farm-dashboard" 
                    className="block text-lg hover:opacity-60 transition-opacity"
                    style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
                  >
                    Farm Dashboard
                  </Link>
                </div>
              </div>

              {/* For Members Section */}
              <div className="pt-4">
                <span 
                  className="text-xs tracking-widest uppercase block mb-4"
                  style={{ color: '#B6A46D', fontFamily: "Arial, sans-serif" }}
                >
                  For Members
                </span>
                <div className="space-y-4 pl-4">
                  <Link 
                    to="/for-customers" 
                    className="block text-lg hover:opacity-60 transition-opacity"
                    style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
                  >
                    Explore More
                  </Link>
                  <Link 
                    to="/subscribe" 
                    className="block text-lg hover:opacity-60 transition-opacity"
                    style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
                  >
                    Subscribe
                  </Link>
                  <Link 
                    to="/marketplace" 
                    className="block text-lg hover:opacity-60 transition-opacity"
                    style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
                  >
                    Exclusive
                  </Link>
                </div>
              </div>

              {/* Free discovery */}
              <div className="pt-4" data-testid="menu-discover-section">
                <span className="text-xs tracking-widest uppercase block mb-4" style={{ color: '#B6A46D', fontFamily: "Arial, sans-serif" }}>
                  Discover · Free
                </span>
                <div className="space-y-4 pl-4">
                  {[["/marketplace/map", "Explore the Land", "menu-explore-land"], ["/marketplace/map?type=farms", "Explore Farms", "menu-explore-farms"],
                    ["/marketplace/map?type=markets", "Explore Farmers Markets", "menu-explore-markets"], ["/explore/wish-list", "My Wish List", "menu-wish-list"],
                    ["/favorites", "Saved Places", "menu-saved-places"], ["/explore/region", "My Alerts", "menu-alerts"]].map(([to, label, tid]) => (
                    <Link key={tid} to={to} data-testid={tid} className="block text-lg hover:opacity-60 transition-opacity" style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}>
                      {label}
                    </Link>
                  ))}
                </div>
              </div>

              <Link 
                to="/contact" 
                className="block text-2xl tracking-wide hover:opacity-60 transition-opacity pt-4"
                style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', serif" }}
              >
                Contact
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}
